import { createElement, setAttributes } from '../../global/render-api';
import { UIElement } from '../ui-element';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import type {
    UICaptionLayout,
    UICaptionLayouts,
    UICaptionPosition,
    UICaptionPositions,
} from './ui-caption.types';
import type { NullOr } from '../../global/utils.type';
import { UIIconColors } from '../icon/ui-icon.helpers';
import { UIIcon } from '../icon/ui-icon';
import { Attributes } from '../ui-element.type';
import styles from './ui-caption.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UICaption
 * @element ui-caption
 * @classdesc Represents a class for <code>ui-caption</code> element.
 * Text label with icon position left of right.
 * @property {("left" | "right")} [pos="left"] {@attr position} Position of icon.
 *  {@desc left}
 *  {@desc right}
 * @property {("chip" | "mark")} [layout] {@attr layout} Layout type.
 *  {@desc chip}
 *  {@desc mark}
 * @slot {@type "ui-icon"}
 * @example
 * <ui-caption>
 *   <ui-icon glyph="home"></ui-icon>
 *   Home insurance.
 * </ui-caption>
 */
@Component({ tag: 'ui-caption', styles })
export class UICaption extends UIElement {
    static readonly Positions: UICaptionPositions = {
        Left: 'left',
        Right: 'right',
    } as const;

    static readonly Layouts: UICaptionLayouts = {
        Chip: 'chip',
        Mark: 'mark',
    } as const;

    @Attribute({ type: String, name: 'position' })
    pos: UICaptionPosition = UICaption.Positions.Left;

    @Attribute(String)
    layout?: UICaptionLayout;

    /**
     * Constructs caption with 'mark' layout.
     * @private
     */
    private renderLayoutMark(): void {
        const existingIcon = this.querySelector<UIIcon>('ui-icon');
        const icon: NullOr<UIIcon> =
            existingIcon ||
            createElement({
                tagName: 'ui-icon',
                attributes: {
                    glyph: 'redesign-checkmark-thick',
                    color: UIIconColors.WHITE,
                    bgcolor: UIIconColors.LILAC,
                    size: 'avatar',
                },
            });

        /**
         * @type {Record<string, string>}
         */
        const attributes: Attributes<string> = {};

        if (icon instanceof UIIcon) {
            if (icon.glyph !== 'redesign-checkmark-thick') {
                attributes.glyph = 'redesign-checkmark-thick';
                if (icon.glyph) {
                    this.warnAttributes('glyph', icon.glyph, [
                        'redesign-checkmark-thick',
                    ]);
                }
            }

            if (icon.size !== 'avatar') {
                attributes.size = 'avatar';
                if (icon.size) {
                    this.warnAttributes('size', icon.size, ['avatar']);
                }
            }
        }

        if (icon instanceof HTMLElement) {
            if (Object.keys(attributes).length) {
                setAttributes(icon, attributes);
            }

            if (this.firstChild !== icon) {
                this.prepend(icon);
            }
        }

        const containerElement = this.parentElement?.parentElement;
        if (containerElement?.localName === 'ui-columns') {
            containerElement.classList.add('value-proposition');
        }
    }

    /**
     * Gives a console warning about deprecated attributes if in debug mode.
     * @private
     * @param {string} key
     * @param {string} value
     * @param {string[]} allowedValues
     */
    private warnAttributes(
        key: string,
        value: string,
        allowedValues: Array<string>
    ): void {
        const allowedOptions = `Allowed options are: ${allowedValues.join(', ')}.`;
        UICaption.DEBUG_MODE &&
            console.warn(
                `${value} - ${key} attribute is not allowed. ${allowedOptions}`,
                this
            );
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        if (this.layout === UICaption.Layouts.Mark) {
            this.renderLayoutMark();
        }
    }
}
